import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  Stack,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarTodayIcon,
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Transcribe as TranscribeIcon,
  Description as DescriptionIcon,
  PlayCircle as PlayCircleIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import BaseDialog from './BaseDialog';
import BaseButton from './BaseButton';

// Composant Shimmer pour l'état de chargement
const UniversalCardSkeleton = ({ type = 'meeting', isFirefox = false }) => {
  const shimmerAnimation = {
    '@keyframes shimmer': {
      '0%': {
        backgroundPosition: '-200px 0'
      },
      '100%': {
        backgroundPosition: 'calc(200px + 100%) 0'
      }
    },
    animation: 'shimmer 2s ease-in-out infinite',
    background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 75%)',
    backgroundSize: '200px 100%',
    backgroundRepeat: 'no-repeat'
  };

  const getBannerGradient = () => {
    switch (type) {
      case 'meeting':
        return 'linear-gradient(135deg, rgba(76,175,80,0.8) 0%, rgba(139,195,74,0.9) 50%, rgba(102,187,106,0.8) 100%)';
      case 'model':
        return 'linear-gradient(135deg, rgba(25,118,210,0.8) 0%, rgba(33,150,243,0.9) 50%, rgba(30,136,229,0.8) 100%)';
      case 'meetingModel':
        return 'linear-gradient(135deg, rgba(156,39,176,0.8) 0%, rgba(142,36,170,0.9) 50%, rgba(123,31,162,0.8) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(224,224,224,0.8) 0%, rgba(189,189,189,0.9) 50%, rgba(158,158,158,0.8) 100%)';
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        // Style Liquid Glass identique à la vraie carte
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
        WebkitBackdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.1),
          0 4px 16px rgba(0, 0, 0, 0.05),
          inset 0 1px 0 rgba(255, 255, 255, 0.3),
          inset 0 0 20px rgba(255, 255, 255, 0.05)
        `,
      }}
    >
      {/* Bande colorée en haut */}
      <Box sx={{ 
        height: 4, 
        background: getBannerGradient(),
        ...shimmerAnimation
      }} />
      
      {/* Contenu principal */}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
        
        {/* Header avec titre et icônes */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            {/* Titre shimmer */}
            <Box sx={{ 
              height: 20, 
              width: '85%', 
              borderRadius: 1, 
              background: 'rgba(255,255,255,0.15)',
              mb: 1,
              ...shimmerAnimation
            }} />
            <Box sx={{ 
              height: 16, 
              width: '60%', 
              borderRadius: 1, 
              background: 'rgba(255,255,255,0.1)',
              ...shimmerAnimation
            }} />
          </Box>
          
          {/* Icônes d'action shimmer */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', ...shimmerAnimation }} />
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', ...shimmerAnimation }} />
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', ...shimmerAnimation }} />
          </Box>
        </Box>

        {/* Participants shimmer */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ 
            height: 14, 
            width: '40%', 
            borderRadius: 1, 
            background: 'rgba(255,255,255,0.1)',
            ...shimmerAnimation
          }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[...Array(5)].map((_, idx) => (
              <Box key={idx} sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                background: getBannerGradient(),
                opacity: 0.3,
                ...shimmerAnimation
              }} />
            ))}
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              background: 'rgba(142, 142, 147, 0.3)',
              ...shimmerAnimation
            }} />
          </Box>
        </Box>

        {/* Footer shimmer */}
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          
          {/* Date et créateur shimmer */}
          <Box sx={{ 
            p: 1.5,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: isFirefox ? 'blur(6px)' : 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            display: 'flex', 
            flexDirection: 'column',
            gap: 0.5,
            minWidth: 120,
            ...shimmerAnimation
          }}>
            <Box sx={{ height: 12, width: '80%', borderRadius: 1, background: 'rgba(255,255,255,0.2)' }} />
            <Box sx={{ height: 12, width: '60%', borderRadius: 1, background: 'rgba(255,255,255,0.15)' }} />
          </Box>
          
          {/* Statuts shimmer */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              background: 'rgba(255, 152, 0, 0.2)',
              ...shimmerAnimation
            }} />
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              background: 'rgba(255, 152, 0, 0.15)',
              ...shimmerAnimation
            }} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// Composant interne qui contient toute la logique
const UniversalCardInternal = React.forwardRef(({
  // Props de base
  type = 'meeting', // 'meeting', 'model', 'meetingModel'
  item,
  
  // Props spécifiques aux réunions
  transcription = null,
  summary = null,
  
  // Props d'actions
  actions = [], // Array des boutons d'action principaux
  menuItems = [], // Array des éléments de menu contextuel
  onAction = () => {}, // Callback pour les actions
  
  // Props de navigation
  onClick = null, // Callback pour le clic sur la card
  
  // Props de style
  className = '',
  sx = {},
  
  // Props de contexte
  tabValue = null, // Pour les réunions (déterminer l'affichage du créateur)
  
  ...otherProps
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, isAdmin } = useAuth();
  const { addSystemNotification } = useNotifications();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [creator, setCreator] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  const open = Boolean(anchorEl);

  // Détection de Firefox pour optimiser les effets CSS
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  // Récupérer les informations du créateur
  useEffect(() => {
    if (!item?.userId && !item?.createdBy) return;
    
    const fetchCreator = async () => {
      try {
        const userId = item.userId || item.createdBy;
        if (userId === currentUser?.uid) {
          setCreator({ displayName: 'Vous' });
          return;
        }
        
        if (userId === 'Admin') {
          setCreator({ displayName: 'Admin' });
          return;
        }
        
        const userRef = doc(db, 'users', userId);
        const userSnapshot = await getDoc(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setCreator({
            displayName: userData.displayName || 
                         `${userData.firstName || ''} ${userData.lastName || ''}`.trim() ||
                         userData.email || 
                         'Utilisateur inconnu'
          });
        } else {
          const profileRef = doc(db, 'profiles', userId);
          const profileSnapshot = await getDoc(profileRef);
          
          if (profileSnapshot.exists()) {
            const profileData = profileSnapshot.data();
            setCreator({
              displayName: profileData.displayName || 
                           `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() ||
                           profileData.email || 
                           'Utilisateur inconnu'
            });
          } else {
            setCreator({ displayName: `${userId.substring(0, 8)}...` });
          }
        }
      } catch (error) {
        if (error.code === 'permission-denied') {
          if (item.creatorEmail) {
            setCreator({ displayName: item.creatorEmail.split('@')[0] });
          } else {
            setCreator(null);
          }
        } else {
          addSystemNotification('Erreur lors de la récupération des infos utilisateur:', 'error');
          setCreator({ displayName: 'Utilisateur inconnu' });
        }
      }
    };
    
    fetchCreator();
  }, [item?.userId, item?.createdBy, currentUser?.uid]);

  // Fonctions utilitaires avec couleurs coordonnées au bord de la carte
  const getAvatarColor = (participantName, cardType = 'meeting') => {
    if (!participantName) return '#8E8E93';
    
    // Couleurs coordonnées aux dégradés de bannière selon le type de carte
    const colorsByType = {
      meeting: [
        // Nuances de vert coordonnées au bord vert des réunions
        'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)', // Green coordonné
        'linear-gradient(135deg, #8BC34A 0%, #9CCC65 100%)', // Light Green coordonné
        'linear-gradient(135deg, #66BB6A 0%, #81C784 100%)', // Medium Green coordonné
        'linear-gradient(135deg, #43A047 0%, #4CAF50 100%)', // Deep Green coordonné
        'linear-gradient(135deg, #388E3C 0%, #43A047 100%)', // Forest Green coordonné
        'linear-gradient(135deg, #2E7D32 0%, #388E3C 100%)', // Dark Green coordonné
        'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)', // Very Dark Green coordonné
        'linear-gradient(135deg, #689F38 0%, #7CB342 100%)', // Olive Green coordonné
      ],
      model: [
        // Nuances de bleu coordonnées au bord bleu des modèles
        'linear-gradient(135deg, #1976D2 0%, #1E88E5 100%)', // Blue coordonné
        'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)', // Dark Blue coordonné
        'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)', // Deep Blue coordonné
        'linear-gradient(135deg, #0277BD 0%, #0288D1 100%)', // Light Blue coordonné
        'linear-gradient(135deg, #01579B 0%, #0277BD 100%)', // Cyan Blue coordonné
        'linear-gradient(135deg, #1E88E5 0%, #2196F3 100%)', // Bright Blue coordonné
        'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)', // Sky Blue coordonné
        'linear-gradient(135deg, #03A9F4 0%, #00BCD4 100%)', // Aqua Blue coordonné
      ],
      meetingModel: [
        // Nuances de violet coordonnées au bord violet des modèles de réunion
        'linear-gradient(135deg, #8E24AA 0%, #9C27B0 100%)', // Purple coordonné
        'linear-gradient(135deg, #7B1FA2 0%, #8E24AA 100%)', // Dark Purple coordonné
        'linear-gradient(135deg, #6A1B9A 0%, #7B1FA2 100%)', // Deep Purple coordonné
        'linear-gradient(135deg, #4A148C 0%, #6A1B9A 100%)', // Very Dark Purple coordonné
        'linear-gradient(135deg, #9C27B0 0%, #AB47BC 100%)', // Light Purple coordonné
        'linear-gradient(135deg, #AB47BC 0%, #BA68C8 100%)', // Bright Purple coordonné
        'linear-gradient(135deg, #BA68C8 0%, #CE93D8 100%)', // Soft Purple coordonné
        'linear-gradient(135deg, #673AB7 0%, #7986CB 100%)', // Indigo Purple coordonné
      ]
    };
    
    const typeColors = colorsByType[cardType] || colorsByType.meeting;
    const index = Array.from(participantName).reduce((acc, char) => acc + char.charCodeAt(0), 0) % typeColors.length;
    return typeColors[index];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Obtenir le dégradé Liquid Glass selon le type
  const getBannerGradient = () => {
    switch (type) {
      case 'meeting':
        // Dégradé selon le statut de transcription
        if (!transcription) return 'linear-gradient(135deg, rgba(224,224,224,0.8) 0%, rgba(189,189,189,0.9) 50%, rgba(158,158,158,0.8) 100%)';
        switch(transcription.status) {
          case 'completed': 
            return 'linear-gradient(135deg, rgba(76,175,80,0.8) 0%, rgba(139,195,74,0.9) 50%, rgba(102,187,106,0.8) 100%)';
          case 'processing': 
            return 'linear-gradient(135deg, rgba(33,150,243,0.8) 0%, rgba(3,169,244,0.9) 50%, rgba(63,81,181,0.8) 100%)';
          case 'error': 
            return 'linear-gradient(135deg, rgba(244,67,54,0.8) 0%, rgba(233,30,99,0.9) 50%, rgba(255,87,34,0.8) 100%)';
          default: 
            return 'linear-gradient(135deg, rgba(224,224,224,0.8) 0%, rgba(189,189,189,0.9) 50%, rgba(158,158,158,0.8) 100%)';
        }
      case 'model':
        return 'linear-gradient(135deg, rgba(25,118,210,0.8) 0%, rgba(33,150,243,0.9) 50%, rgba(30,136,229,0.8) 100%)';
      case 'meetingModel':
        return 'linear-gradient(135deg, rgba(156,39,176,0.8) 0%, rgba(142,36,170,0.9) 50%, rgba(123,31,162,0.8) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(224,224,224,0.8) 0%, rgba(189,189,189,0.9) 50%, rgba(158,158,158,0.8) 100%)';
    }
  };

  // Obtenir le statut de la transcription pour les réunions avec icônes élégantes
  const getTranscriptionStatus = () => {
    if (type !== 'meeting' || !transcription) return null;
    
    // Utiliser validationStatus en priorité s'il existe, sinon fallback sur status
    const currentStatus = transcription.validationStatus || transcription.status;
    
    switch(currentStatus) {
      case 'completed':
        return { 
          status: 'completed', 
          color: '#ff9800', 
          icon: <TranscribeIcon fontSize="small" />,
          tooltip: 'Transcription effectuée'
        };
      case 'processing':
        return { 
          status: 'processing', 
          color: '#2196f3', 
          icon: <PlayCircleIcon fontSize="small" />,
          tooltip: 'Transcription en cours'
        };
      case 'processing_final':
        return { 
          status: 'processing_final', 
          color: '#9c27b0', 
          icon: <PlayCircleIcon fontSize="small" />,
          tooltip: 'Finalisation de la transcription...'
        };
      case 'error':
        return { 
          status: 'error', 
          color: '#f44336', 
          icon: <HelpIcon fontSize="small" />,
          tooltip: 'Erreur de transcription'
        };
      default:
        return { 
          status: 'pending', 
          color: '#9e9e9e', 
          icon: <AccessTimeIcon fontSize="small" />,
          tooltip: 'Transcription en attente'
        };
    }
  };

  // Obtenir le statut du résumé pour les réunions avec icônes élégantes
  const getSummaryStatus = () => {
    if (type !== 'meeting') return null;
    
    if (!summary) {
      return { 
        status: 'not-generated', 
        color: '#9e9e9e', 
        icon: <DescriptionIcon fontSize="small" />,
        tooltip: 'Résumé non généré'
      };
    }
    
    switch(summary.status) {
      case 'Terminé':
        return { 
          status: 'completed', 
          color: '#ff9800', 
          icon: <AutoAwesomeIcon fontSize="small" />,
          tooltip: 'Résumé généré'
        };
      case 'En cours':
        return { 
          status: 'processing', 
          color: '#2196f3', 
          icon: <PlayCircleIcon fontSize="small" />,
          tooltip: 'Résumé en cours de génération'
        };
      case 'Erreur':
        return { 
          status: 'error', 
          color: '#f44336', 
          icon: <HelpIcon fontSize="small" />,
          tooltip: 'Erreur de génération du résumé'
        };
      default:
        return { 
          status: 'processing', 
          color: '#2196f3', 
          icon: <PlayCircleIcon fontSize="small" />,
          tooltip: 'Résumé en cours de génération'
        };
    }
  };

  // Formater la date
  const formatDate = () => {
    const dateValue = item.date || item.createdAt;
    if (!dateValue) return 'Date non disponible';
    
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short', 
        year: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Gestionnaires d'événements
  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action, menuItem) => {
    handleCloseMenu();
    if (menuItem.action === 'delete') {
      setConfirmDeleteOpen(true);
    } else {
      onAction(action, item, menuItem);
    }
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    onAction('delete', item);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleActionClick = (event, action) => {
    event.stopPropagation();
    onAction(action.type, item, action);
  };

  // Variables pour l'affichage conditionnel
  const transcriptionStatusInfo = getTranscriptionStatus();
  const summaryStatusInfo = getSummaryStatus();
  const shouldShowCreator = creator;
  const hasExpandableContent = item.context || item.description || (item.participants && item.participants.length > 0) || (item.tags && item.tags.length > 0);

  return (
    <Paper 
      ref={ref}
      elevation={0}
      className={className}
      sx={{ 
        borderRadius: 3, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        // Style Liquid Glass optimisé selon le navigateur
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
        WebkitBackdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.1),
          0 4px 16px rgba(0, 0, 0, 0.05),
          inset 0 1px 0 rgba(255, 255, 255, 0.3),
          inset 0 0 20px rgba(255, 255, 255, 0.05)
        `,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        // Optimisation spécifique Firefox pour éviter le flou au hover
        ...(isFirefox ? {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `
              0 12px 32px rgba(0, 0, 0, 0.12),
              0 6px 16px rgba(0, 0, 0, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.4)
            `,
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }
        } : {
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: `
              0 20px 48px rgba(0, 0, 0, 0.15),
              0 8px 24px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.4),
              inset 0 0 24px rgba(255, 255, 255, 0.08)
            `,
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }
        }),
        // Effet de reflet mobile - désactivé sur Firefox pour éviter les conflits
        ...(!isFirefox && {
          '&::before': {
            content: '""',
            position: 'absolute',  
            top: '0%',
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'left 0.5s ease-in-out',
            zIndex: 1,
            pointerEvents: 'none',
          },
          '&:hover::before': {
            left: '100%',
          }
        }),
        ...sx
      }}
      onClick={handleCardClick}
      {...otherProps}
    >
      {/* Bandeau supérieur avec dégradé Liquid Glass */}
      <Box 
        sx={{ 
          height: 8,
          width: '100%',
          background: getBannerGradient(),
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, transparent 50%, rgba(255,255,255,0.3) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%, 100%': { opacity: 0.3 },
            '50%': { opacity: 0.7 },
          }
        }}
      />
      
      <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
        {/* Header avec titre, icône de confidentialité et actions dans une box flottante */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: isFirefox ? 'blur(5px)' : 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {/* Icône de confidentialité */}
            {item?.isConfidential && (
              <Tooltip title="Réunion confidentielle">
                <Box sx={{
                  p: 0.5,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(142, 36, 170, 0.2) 100%)',
                  backdropFilter: isFirefox ? 'blur(3px)' : 'blur(6px)',
                  border: '1px solid rgba(156, 39, 176, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <LockIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                </Box>
              </Tooltip>
            )}
            <Typography 
              variant="subtitle1" 
              fontWeight={700} 
              sx={{ 
                lineHeight: 1.3,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-0.02em'
              }}
            >
              {item?.title || 'Titre non disponible'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {actions.map((action, index) => (
              <Tooltip key={index} title={action.tooltip || action.label}>
                <IconButton 
                  size="small" 
                  onClick={(e) => handleActionClick(e, action)}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: isFirefox ? 'blur(4px)' : 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: action.color === 'info' ? '#2196f3' : 'inherit',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                      transform: isFirefox ? 'scale(1.05)' : 'scale(1.1)',
                    }
                  }}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
            {menuItems.length > 0 && (
              <Tooltip title="Plus d'actions">
                <IconButton 
                  size="small" 
                  onClick={handleOpenMenu}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: isFirefox ? 'blur(4px)' : 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                      transform: isFirefox ? 'scale(1.05)' : 'scale(1.1)',
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Description/Contexte dans une box flottante */}
        {(item.context || item.description) && (
          <Box sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.06)',
            backdropFilter: isFirefox ? 'blur(4px)' : 'blur(8px)', 
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: isMobile && !expanded ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {item.context || item.description}
            </Typography>
          </Box>
        )}
        
        {/* Tags (pour les modèles de prompt) dans une box flottante */}
        {type === 'model' && item.tags && item.tags.length > 0 && (!isMobile || expanded) && (
          <Box sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            background: 'rgba(25, 118, 210, 0.08)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(25, 118, 210, 0.2)',
            boxShadow: 'inset 0 1px 0 rgba(25, 118, 210, 0.3)',
          }}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {item.tags.slice(0, 3).map((tag, idx) => (
                <Chip 
                  key={idx} 
                  label={tag} 
                  size="small" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 22,
                    background: 'rgba(25, 118, 210, 0.15)',
                    backdropFilter: 'blur(6px)',
                    color: '#1976d2',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(25, 118, 210, 0.25)',
                      transform: 'scale(1.05)',
                    }
                  }}
                />
              ))}
              {item.tags.length > 3 && (
                <Chip 
                  label={`+${item.tags.length - 3}`} 
                  size="small" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 22,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Aperçu du prompt dans une box spéciale */}
        {type === 'model' && item.prompt && (!isMobile || expanded) && (
          <Box sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, rgba(25, 118, 210, 0.03) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(25, 118, 210, 0.15)',
            boxShadow: 'inset 0 1px 0 rgba(25, 118, 210, 0.2)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.5), transparent)',
              borderRadius: '2px 2px 0 0',
            }
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                color: 'rgba(25, 118, 210, 0.9)',
                fontFamily: '"SF Mono", Consolas, monospace',
                fontSize: '0.75rem',
              }}
            >
              "{item.prompt.substring(0, 100)}{item.prompt.length > 100 ? '...' : ''}"
            </Typography>
          </Box>
        )}

        {/* Barre de progression flottante */}
        {type === 'meeting' && transcription && transcription.status === 'processing' && transcription.progress && (!isMobile || expanded) && (
          <Box sx={{ 
            width: '100%', 
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            background: 'rgba(33, 150, 243, 0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(33, 150, 243, 0.2)',
          }}>
            <LinearProgress 
              variant="determinate" 
              value={transcription.progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                background: 'rgba(33, 150, 243, 0.15)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #2196f3 0%, #03a9f4 100%)',
                  borderRadius: 3,
                  boxShadow: '0 0 12px rgba(33, 150, 243, 0.4)',
                }
              }} 
            />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', mt: 0.5, color: '#2196f3', fontWeight: 600 }}>
              {Math.round(transcription.progress)}% terminé
            </Typography>
          </Box>
        )}

        {/* Bouton d'expansion mobile */}
        {isMobile && hasExpandableContent && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{ 
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.25)',
                  transform: expanded ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                }
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Participants simple sans box */}
        {item.participants && item.participants.length > 0 && (!isMobile || expanded) && (
          <Box sx={{ 
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
               // fontWeight: 700, 
                color: 'rgba(0, 0, 0, 0.8)',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              👥 Participants ({item.participants.length})
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0,
              '& > *:not(:first-of-type)': {
                marginLeft: '-8px'
              }
            }}>
              {item.participants.slice(0, 6).map((participant, idx) => {
                const participantName = participant.displayName || participant.name || participant.title || participant;
                return (
                  <Tooltip 
                    key={idx} 
                    title={participantName}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          background: 'rgba(0, 0, 0, 0.85)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        fontSize: '0.75rem', 
                        fontWeight: 700,
                        background: getBannerGradient(),
                        color: 'white',
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        zIndex: 10 - idx,
                        position: 'relative',
                        '&:hover': {
                          transform: 'scale(1.15) translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                          zIndex: 20,
                        }
                      }}
                    >
                      {getInitials(participantName)}
                    </Avatar>
                  </Tooltip>
                );
              })}
              
              {/* Affichage "+X" pour les participants restants */}
              {item.participants.length > 6 && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                        Autres participants :
                      </Typography>
                      {item.participants.slice(6).map((participant, idx) => {
                        const participantName = participant.displayName || participant.name || participant.title || participant;
                        return (
                          <Typography 
                            key={idx} 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              color: 'rgba(255, 255, 255, 0.9)',
                              lineHeight: 1.4,
                              mb: idx < item.participants.slice(6).length - 1 ? 0.5 : 0
                            }}
                          >
                            • {participantName}
                          </Typography>
                        );
                      })}
                    </Box>
                  }
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        background: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        maxWidth: '200px'
                      }
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, rgba(142, 142, 147, 0.8) 0%, rgba(118, 118, 128, 0.9) 100%)',
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      zIndex: 4,
                      marginLeft: '-8px',
                      '&:hover': {
                        transform: 'scale(1.15) translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                        background: 'linear-gradient(135deg, rgba(142, 142, 147, 0.9) 0%, rgba(118, 118, 128, 1) 100%)',
                        zIndex: 20,
                      }
                    }}
                  >
                    +{item.participants.length - 6}
                  </Avatar>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {/* Footer restructuré avec boxes séparées */}
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          
          {/* Ligne principale avec informations essentielles et statuts */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            
            {/* Informations à gauche */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Date et Créateur dans une box groupée */}
              <Box sx={{ 
                p: 1.5,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: isFirefox ? 'blur(6px)' : 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                display: 'flex', 
                flexDirection: 'column',
                gap: 0.5,
              }}>
                {/* Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.6)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                    {formatDate()}
                  </Typography>
                </Box>
                
                {/* Créateur */}
                {shouldShowCreator && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.6)' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                      {creator.displayName === currentUser?.displayName || creator.displayName === currentUser?.email?.split('@')[0] 
                        ? 'Vous' 
                        : creator.displayName}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Durée pour les réunions dans sa propre box */}
              {type === 'meeting' && item.duration && (
                <Box sx={{ 
                  p: 1,
                  borderRadius: 1.5,
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: isFirefox ? 'blur(4px)' : 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                }}>
                  <TimerIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.6)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                    {item.duration}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Icônes de statut à droite - empilées verticalement */}
            {type === 'meeting' && (transcriptionStatusInfo || summaryStatusInfo) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
                {transcriptionStatusInfo && (
                  <Tooltip 
                    title={transcriptionStatusInfo.tooltip}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          background: 'rgba(0, 0, 0, 0.85)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          px: 1.5,
                          py: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: `${transcriptionStatusInfo.color}15`,
                      border: `2px solid ${transcriptionStatusInfo.color}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: `${transcriptionStatusInfo.color}25`,
                        border: `2px solid ${transcriptionStatusInfo.color}60`,
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${transcriptionStatusInfo.color}40`,
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(transcriptionStatusInfo.status === 'processing' && {
                          '@keyframes breathe': {
                            '0%, 100%': { 
                              transform: 'scale(1)',
                              opacity: 0.8
                            },
                            '50%': { 
                              transform: 'scale(1.4)',
                              opacity: 1
                            }
                          },
                          animation: 'breathe 1s ease-in-out infinite'
                        }),
                        ...(transcriptionStatusInfo.status === 'processing_final' && {
                          '@keyframes finalizing': {
                            '0%': { 
                              transform: 'scale(1) rotate(0deg)',
                              opacity: 0.7
                            },
                            '25%': { 
                              transform: 'scale(1.2) rotate(90deg)',
                              opacity: 0.9
                            },
                            '50%': { 
                              transform: 'scale(1.1) rotate(180deg)',
                              opacity: 1
                            },
                            '75%': { 
                              transform: 'scale(1.2) rotate(270deg)',
                              opacity: 0.9
                            },
                            '100%': { 
                              transform: 'scale(1) rotate(360deg)',
                              opacity: 0.7
                            }
                          },
                          animation: 'finalizing 2s ease-in-out infinite'
                        })
                      }}>
                        {React.cloneElement(transcriptionStatusInfo.icon, { 
                          sx: { 
                            color: transcriptionStatusInfo.color, 
                            fontSize: 18,
                            fontWeight: 600
                          } 
                        })}
                      </Box>
                    </Box>
                  </Tooltip>
                )}
                {summaryStatusInfo && (
                  <Tooltip 
                    title={summaryStatusInfo.tooltip}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          background: 'rgba(0, 0, 0, 0.85)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          px: 1.5,
                          py: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: `${summaryStatusInfo.color}15`,
                      border: `2px solid ${summaryStatusInfo.color}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: `${summaryStatusInfo.color}25`,
                        border: `2px solid ${summaryStatusInfo.color}60`,
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${summaryStatusInfo.color}40`,
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(summaryStatusInfo.status === 'processing' && {
                          '@keyframes pulse': {
                            '0%, 100%': { 
                              transform: 'scale(1)',
                              opacity: 0.7
                            },
                            '50%': { 
                              transform: 'scale(1.5)',
                              opacity: 1
                            }
                          },
                          animation: 'pulse 1.2s ease-in-out infinite'
                        })
                      }}>
                        {React.cloneElement(summaryStatusInfo.icon, { 
                          sx: { 
                            color: summaryStatusInfo.color, 
                            fontSize: 18,
                            fontWeight: 600
                          } 
                        })}
                      </Box>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

        </Box>
      </Box>
       
      {/* Menu d'actions avec effet liquid glass */}
      {menuItems.length > 0 && (
        <Menu
          id={`universal-card-menu-${item.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          PaperProps={{
            elevation: 0,
            sx: { 
              minWidth: 220, 
              mt: 1, 
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
              WebkitBackdropFilter: isFirefox ? 'blur(10px)' : 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: isFirefox 
                ? `0 8px 20px rgba(0, 0, 0, 0.10), 0 4px 12px rgba(0, 0, 0, 0.06)`
                : `0 16px 40px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)`,
            }
          }}
        >
          {menuItems.map((menuItem, index) => (
            <MenuItem 
              key={index} 
              onClick={() => handleMenuAction(menuItem.action, menuItem)} 
              sx={{ 
                py: 1.5,
                px: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: isFirefox ? 'blur(6px)' : 'blur(12px)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: '40px' }}>
                <Box sx={{
                  p: 0.5,
                  borderRadius: 1.5,
                  background: menuItem.action === 'delete' 
                    ? 'rgba(244, 67, 54, 0.1)' 
                    : 'rgba(33, 150, 243, 0.1)',
                  backdropFilter: 'blur(6px)',
                }}>
                  {React.cloneElement(menuItem.icon, { 
                    fontSize: 'small', 
                    sx: { color: menuItem.color || 'inherit' } 
                  })}
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary={menuItem.label} 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: menuItem.action === 'delete' ? '#f44336' : 'inherit'
                }} 
              />
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Dialog de confirmation avec effet liquid glass */}
      <BaseDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="🗑️ Confirmer la suppression"
        subtitle="Cette action est irréversible"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <BaseButton 
              onClick={() => setConfirmDeleteOpen(false)}
              variant="outlined"
              sx={{ 
                px: 4, 
                py: 1,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              Annuler
            </BaseButton>
            <BaseButton 
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{ 
                px: 4, 
                py: 1,
                background: 'linear-gradient(135deg, rgba(244,67,54,0.9) 0%, rgba(211,47,47,0.9) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(244,67,54,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(211,47,47,0.9) 0%, rgba(198,40,40,0.9) 100%)',
                }
              }}
            >
              Supprimer
            </BaseButton>
          </Box>
        }
        sx={{
          '& .MuiDialog-paper': {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(244, 67, 54, 0.2)',
          }
        }}
      >
        <Box sx={{ 
          textAlign: 'center',
          py: 3,
          background: 'linear-gradient(145deg, rgba(255,245,245,0.6) 0%, rgba(255,235,235,0.8) 100%)',
          borderRadius: 3,
          border: '1px solid rgba(244, 67, 54, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#d32f2f' }}>
            ⚠️ Attention !
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Êtes-vous sûr de vouloir supprimer définitivement cet élément ?<br/>
            <strong>Cette action ne peut pas être annulée.</strong>
          </Typography>
        </Box>
      </BaseDialog>
    </Paper>
  );
});

UniversalCardInternal.displayName = 'UniversalCardInternal';

// Composant wrapper principal avec gestion du shimmer
const UniversalCard = React.forwardRef(({
  // Props d'état
  isLoading = false, // Afficher le shimmer loading
  
  // Toutes les autres props
  ...props
}, ref) => {
  // Détection de Firefox pour le shimmer
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  // Afficher le shimmer si loading
  if (isLoading) {
    return <UniversalCardSkeleton type={props.type || 'meeting'} isFirefox={isFirefox} />;
  }

  // Sécurité : si item n'est pas défini, retourner null
  if (!props.item) {
    return null;
  }

  // Sinon, rendre le composant normal
  return <UniversalCardInternal ref={ref} {...props} />;
});

UniversalCard.displayName = 'UniversalCard';

export default UniversalCard; 